/**
 * The development brain for one website.
 *
 * The scanner card above this one says what a site is built with. This one
 * says what is wrong with it in the owner's own words, with a link to each
 * piece of proof - the material a cold email or a call opens on.
 *
 * Three things about this card are deliberate and easy to undo by accident:
 *
 * 1. IT NEVER RUNS ITSELF. Opening a company must not send the platform out to
 *    crawl their website. The stored row is read on mount; the run is a button,
 *    and only an operator with `manage:ariregister` sees it.
 * 2. IT SURVIVES LEAVING THE PAGE. The run lives in a database row, so coming
 *    back mid-run picks the poll up again instead of showing an empty card.
 * 3. A DEAD RUN NEVER LOOKS BUSY. The API maps an abandoned row to an error on
 *    the way out, so this card cannot show a spinner that turns forever.
 *
 * R3 added the thinking half: the fit verdict sits above the tabs (a verdict
 * behind a tab is a verdict nobody reads until after they have dialled), and
 * the model's reading of the site gets its own tabs beside the measured ones.
 * The email and call script are R4/R5 and slot in the same way.
 *
 * TWO LAYOUTS, ONE CARD. `nav="rail"` pins the tabs down the left, for the page
 * that is nothing but this card; everywhere else they stay a pill row above the
 * panel. Both read the same list and render the same body (`BrainPanels`), so a
 * tenth tab is added once.
 *
 * TWO BUTTONS, TWO COSTS. "Run" goes back out to the prospect's website and
 * spends Cloudflare browser time, which on the free plan is ten minutes a day
 * for the whole account. "Think again" re-runs only the model passes over the
 * pages already stored. They are deliberately not one button with a menu: the
 * expensive one must be the one you have to mean.
 */
import { useMemo, useState } from "react";
import { AlertTriangle, Brain, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { Button, cn } from "@wa/ui";
import { starterLabel } from "../../../../lib/brainStarter";
import type { SiteBrain } from "../../../../lib/siteBrain";
import { AiTestCallButton } from "../../../telephony/aiTestCall";
import { PillTabs, SideTabs, StatusPill, useToast, type PillTab } from "../../../ui";
import type { T } from "../../site-scanner/types";
import { BrainCostLine } from "./BrainCost";
import { BrainFit } from "./BrainFit";
import { BrainPlanBox } from "./BrainPlan";
import { BrainPanel, brainTabs, type BrainTab } from "./BrainPanels";
import { useBrain } from "./useBrain";
import { useBrainHistory } from "./useHistory";
import { usePeerComparisons } from "./usePeers";

type Tab = BrainTab;

export function BrainCard({
  domain,
  canManage,
  me,
  t,
  aiTestCall = true,
  nav = "pills",
}: {
  domain: string | null;
  canManage: boolean;
  /** The reader's own user id, so their own run says "you" and asks nobody. */
  me: string;
  t: T;
  /**
   * Offer the AI test call for this domain. The button draws itself only for
   * `manage:crm.calls`; false where the page already offers the call (the
   * company card above this one).
   */
  aiTestCall?: boolean;
  /**
   * How the tabs are drawn. "rail" pins them down the left of the panel and is
   * for the card's own page, where there is width to spare and the panels are
   * long enough that the strip scrolls out of reach. The dialog and the stacked
   * register card keep "pills": a 214px column there is width taken off a panel
   * that is already narrow, and a pin inside a modal has nothing to travel in.
   */
  nav?: "pills" | "rail";
}) {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>("arguments");

  // A run that finished while the operator was looking at this card deserves to
  // say so. This is the toast idiom the rest of the CRM uses - the operator
  // pressed a button and the result arrived - rather than a background
  // notification, which this app has no mechanism for and which R6 will add
  // through the shell's jobs card instead.
  const { brain, loading, running, starting, errorKey, start, rethink, reload } = useBrain(domain ?? "", (next) => {
    toast(
      next.status === "error"
        ? t("brain.toast.failed", { domain: next.domain })
        : t("brain.toast.done", { domain: next.domain, count: next.findings.length }),
    );
  });

  // Fetched only when the findings tab is open. The comparison is a sentence
  // for a call, not something the card needs before somebody asks to see the
  // defects - and the request costs three queries on the server.
  const comparisons = usePeerComparisons(domain ?? "", tab === "findings" && !!brain);

  // Same rule, same reason. The reload key is the run's generation and status, so
  // a run that lands while the operator is sitting on this tab brings its own
  // entry with it instead of leaving the list one run out of date.
  const history = useBrainHistory(
    domain ?? "",
    tab === "history" && !!brain,
    `${brain?.runSeq ?? 0}:${brain?.status ?? ""}`,
  );

  // One list, both layouts. The pinned menu wants the icons; the pill row has
  // no use for them and simply leaves them behind.
  const tabs = useMemo(() => brainTabs(brain, t), [brain, t]);
  const pills = useMemo<PillTab<Tab>[]>(
    () => tabs.map(({ value, label, count }) => ({ value, label, count })),
    [tabs],
  );

  const reloadLink = (
    <button
      type="button"
      className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      onClick={() => void reload()}
    >
      {t("brain.reload")}
    </button>
  );

  // No domain, nothing to think about. The register holds no website for this
  // company, which the scan card above has already said.
  if (!domain) return null;

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Brain className="size-4 text-primary" /> {t("brain.title")}
          {brain && !running ? <Age brain={brain} t={t} /> : null}
          {/* Beside the age and not under it. The two answer the same operator
              question - can I trust this, and whose work is it - and a run in
              flight keeps saying it, because "did a colleague already start
              this" is worth most while it is still running. */}
          {brain ? <Starter brain={brain} me={me} t={t} /> : null}
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          {/* The AI test call reads this card's arguments and script, so it is
              offered where they are read. It rings a test number only. */}
          {aiTestCall ? <AiTestCallButton target={{ domain }} /> : null}
        {canManage ? (
          <div className="flex flex-wrap items-center gap-2">
            {/* Only offered once there are stored pages to think about again.
                Without a crawl the endpoint refuses, and a button whose only
                outcome is an error message is worse than no button. */}
            {brain?.crawl ? (
              <Button
                variant="ghost"
                size="sm"
                title={t("brain.rethinkHint")}
                onClick={() => void rethink()}
                disabled={running || starting}
              >
                <Sparkles className="size-3.5" />
                {t("brain.rethink")}
              </Button>
            ) : null}

            <Button variant="outline" size="sm" onClick={() => void start()} disabled={running || starting}>
              <RefreshCw className={cn("size-3.5", (running || starting) && "animate-spin")} />
              {t(running || starting ? "brain.running" : brain ? "brain.rerun" : "brain.run")}
            </Button>
          </div>
        ) : null}
        </div>
      </div>

      {errorKey ? (
        <p className="crm-alert">
          <AlertTriangle className="size-4 shrink-0" />
          {t(errorKey)}
        </p>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-8 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
        </div>
      ) : null}

      {/* The empty state names what pressing the button will do. An operator who
          cannot tell what a run costs will not press it, and a card that only
          says "no data" reads as broken rather than as waiting. */}
      {!loading && !brain && !running ? (
        <p className="rounded-xl border border-dashed border-border bg-card/60 px-4 py-5 text-sm text-muted-foreground">
          {t(canManage ? "brain.hint" : "brain.hintReadOnly", { domain })}
        </p>
      ) : null}

      {running ? (
        <p className="flex items-center gap-2 rounded-xl border border-dashed border-border bg-card/60 px-4 py-5 text-sm text-muted-foreground">
          <Loader2 className="size-4 shrink-0 animate-spin" />
          {t("brain.runningHint", { domain })}
        </p>
      ) : null}

      {brain && !running ? (
        <>
          {brain.status === "error" ? (
            <p className="crm-alert">
              <AlertTriangle className="size-4 shrink-0" />
              {t(`brain.runError.${brain.error ?? "failed"}`)}
            </p>
          ) : null}

          {/* A degraded run is still a usable run, but it has to say which part
              is missing - otherwise a short finding list reads as a clean site. */}
          {brain.degraded.length > 0 ? (
            <p className="text-xs text-muted-foreground">
              {/* Named in the operator's words, not the column's. "layout,
                  shots" explains nothing to somebody asking why their
                  screenshots are missing. */}
              {t("brain.degraded", {
                stages: brain.degraded.map((stage) => t(`brain.degradedStage.${stage}`)).join(", "),
              })}
            </p>
          ) : null}

          {/* Above the tabs, always. A "do not call this company" verdict is
              worth nothing if it is one click away from being missed. */}
          {brain.synthesis ? <BrainFit synthesis={brain.synthesis} t={t} /> : null}

          {/* Also above the tabs, and for the same reason: a follow-up somebody
              already agreed is the single most important thing to know before
              ringing this company again, and it must not be behind a tab. */}
          <BrainPlanBox
            domain={brain.domain}
            plan={brain.plan}
            canManage={canManage}
            onChanged={() => void reload()}
            t={t}
          />

          {/* The pictures have been swept, which is not the same as a run that
              never took any. Said out loud, because a card whose screenshots
              have silently gone missing reads as broken and gets the expensive
              button pressed to find out why. */}
          {brain.purgedAt ? (
            <p className="text-xs text-muted-foreground">
              {t("brain.purged", { date: brain.purgedAt.slice(0, 10) })}
            </p>
          ) : null}

          {nav === "rail" ? (
            /* The menu IS the grid item, on purpose: its containing block is
               then the grid area, which is as tall as the panel beside it, and
               that is the distance the pin gets to travel. Wrap it in a div and
               `items-start` shrinks that div to the menu's own height, so the
               pin comes unstuck after one screenful. */
            <div className="grid items-start gap-5 lg:grid-cols-[214px_1fr]">
              <SideTabs tabs={tabs} value={tab} onChange={setTab} label={t("brain.title")} />
              <div className="min-w-0 space-y-4">
                <div className="flex justify-end">{reloadLink}</div>
                <BrainPanel
                  tab={tab}
                  brain={brain}
                  canManage={canManage}
                  me={me}
                  comparisons={comparisons}
                  history={history}
                  onChanged={() => void reload()}
                  t={t}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <PillTabs tabs={pills} value={tab} onChange={setTab} small />
                {reloadLink}
              </div>
              <BrainPanel
                tab={tab}
                brain={brain}
                canManage={canManage}
                me={me}
                comparisons={comparisons}
                history={history}
                onChanged={() => void reload()}
                t={t}
              />
            </>
          )}

          {/* Last line of the card, small. Nobody has to study it; it just has
              to be there, so the two expensive buttons at the top are never
              pressed by somebody with no idea what the last press cost. */}
          <BrainCostLine cost={brain.cost} t={t} />
        </>
      ) : null}
    </section>
  );
}

/**
 * Who started the run this card is showing.
 *
 * The same rule the brain's list uses, drawn as a plain word rather than a
 * pill: it is context, not a state anybody has to act on, and a third pill
 * beside the title would compete with the two that do carry warnings.
 *
 * THE NAME IS A PARAMETER, NEVER A KEY. A colleague called "Auto" must not be
 * translated into the autopilot, which is why the label splits the two cases
 * before either reaches the catalogue.
 */
function Starter({ brain, me, t }: { brain: SiteBrain; me: string; t: T }) {
  const label = starterLabel(brain, me);
  if (!label) return null;
  return (
    <span className="text-xs font-normal text-muted-foreground">
      {"name" in label ? t("brain.startedBy.name", { name: label.name }) : t(`brain.startedBy.${label.key}`)}
    </span>
  );
}

/**
 * How old the answer is.
 *
 * Findings go stale in a way scan results do not: a 404 the owner fixed last
 * week is the worst possible opening line on a call, so the age is next to the
 * title rather than buried at the bottom.
 */
function Age({ brain, t }: { brain: SiteBrain; t: T }) {
  if (!brain.finishedAt) return null;
  const days = Math.floor((Date.now() - new Date(brain.finishedAt).getTime()) / 86_400_000);
  return (
    <StatusPill tone={days > 7 ? "wt" : "mut"}>
      {days <= 0 ? t("brain.age.today") : t("brain.age.days", { n: days })}
    </StatusPill>
  );
}
